// The codes people read and type: the user code an MFD shows for a QR sign-in (RFC 8628), and the reference an owner
// quotes about diagnostics they sent.

/** User codes' letters: no vowels, so they never spell words (RFC 8628 §6.1), as TUNE Live's stand-in has them. */
export const USER_CODE_LETTERS = 'BCDFGHJKLMNPQRSTVWXZ';
/** References' characters: no vowels, and no 0 or 1, which read as O and I. */
export const REFERENCE_CHARACTERS = 'BCDFGHJKLMNPQRSTVWXZ23456789';

/** `count` characters from `alphabet`, each as likely as any other. */
export function randomFrom(alphabet: string, count: number): string {
  // A byte at or above the largest multiple of the alphabet's length is drawn again, so no character is likelier.
  const limit = 256 - (256 % alphabet.length);
  let out = '';
  while (out.length < count) {
    // At most 65,536 bytes a draw: getRandomValues takes no more.
    const bytes = new Uint8Array(Math.min(65_536, (count - out.length) * 2));
    for (const b of crypto.getRandomValues(bytes)) {
      if (b < limit && out.length < count) {
        out += alphabet[b % alphabet.length];
      }
    }
  }
  return out;
}

/** A new user code: eight letters, "BCDF-GHJK". */
export function newUserCode(): string {
  const letters = randomFrom(USER_CODE_LETTERS, 8);
  return `${letters.slice(0, 4)}-${letters.slice(4)}`;
}

/** A user code as someone typed it, with or without its hyphen, in either case: as the cloud writes it, or null. */
export function readUserCode(typed: string): string | null {
  const letters = typed.replace(/[\s-]/g, '').toUpperCase();
  if (letters.length !== 8 || [...letters].some((c) => !USER_CODE_LETTERS.includes(c))) {
    return null;
  }
  return `${letters.slice(0, 4)}-${letters.slice(4)}`;
}

/** A user code as the QR code's address carries it: "BCDFGHJK". */
export function compactUserCode(code: string): string {
  return code.replace('-', '');
}

/** A new reference: "D-7F3K9B". Six characters, so they don't repeat. */
export function newReference(): string {
  return `D-${randomFrom(REFERENCE_CHARACTERS, 6)}`;
}

/** A reference as someone typed it, with or without its "D-", in either case: as the cloud writes it, or null. */
export function readReference(typed: string): string | null {
  let rest = typed.replace(/\s/g, '').toUpperCase();
  if (rest.startsWith('D-')) {
    rest = rest.slice(2);
  }
  if (rest.length < 4 || rest.length > 8 || [...rest].some((c) => !REFERENCE_CHARACTERS.includes(c))) {
    return null;
  }
  return `D-${rest}`;
}
